// @ts-ignore
/* eslint-disable */
/**
 * 认证与用户会话相关 API：/users/me、Token 刷新、登出等。
 * 其它业务域接口按模块拆在同级目录（villageProducts、onboarding 等）。
 */
import { request } from '@umijs/max';
import { hasAdminConsoleAccess } from '@/constants/adminAuth';
import {
  clearUserTokens,
  getUserRefreshToken,
  mapPrimaryRoleToAccess,
  setUserTokens,
} from '@/services/auth';

type ApiEnvelope<T> = {
  code: number;
  message: string;
  data: T;
};

const normalizeCurrentUser = (
  user?: API.CurrentUser,
): API.CurrentUser | undefined => {
  if (!user) {
    return undefined;
  }

  return {
    ...user,
    userid: user.id,
    phone: user.mobile,
    menuCodes: user.menuCodes ?? [],
    actionCodes: user.actionCodes ?? [],
    access: mapPrimaryRoleToAccess(user.primaryRole),
  };
};

/** 获取当前登录用户 GET /api/v1/users/me */
export async function queryCurrentUser(options?: { [key: string]: any }) {
  const response = await request<ApiEnvelope<API.CurrentUser>>(
    '/api/v1/users/me',
    {
      method: 'GET',
      ...(options || {}),
    },
  );

  const currentUser = normalizeCurrentUser(response.data);
  if (currentUser && !hasAdminConsoleAccess(currentUser)) {
    clearUserTokens();
    return {
      ...response,
      data: undefined,
    };
  }

  return {
    ...response,
    data: currentUser,
  };
}

/** 退出登录接口 DELETE /api/v1/auth/sessions/current */
export async function logoutUser(options?: { [key: string]: any }) {
  const response = await request<ApiEnvelope<Record<string, any>>>(
    '/api/v1/auth/sessions/current',
    {
      method: 'DELETE',
      ...(options || {}),
    },
  );

  clearUserTokens();
  return response.data;
}

/** 用户登录接口 POST /api/v1/auth/sessions */
export async function loginWithUser(
  body: API.LoginParams,
  options?: { [key: string]: any },
) {
  const response = await request<
    ApiEnvelope<{
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresInSeconds: number;
      refreshTokenExpiresInSeconds: number;
      currentUser: API.CurrentUser;
    }>
  >('/api/v1/auth/sessions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      mobile: body.mobile,
      code: body.code,
      password: body.password,
    },
    ...(options || {}),
  });

  const currentUser = normalizeCurrentUser(response.data.currentUser);
  const canAccessConsole = hasAdminConsoleAccess(currentUser);

  if (response.data.accessToken && canAccessConsole) {
    setUserTokens(
      response.data.accessToken,
      response.data.refreshToken,
      response.data.accessTokenExpiresInSeconds,
      response.data.refreshTokenExpiresInSeconds,
    );
  } else if (!canAccessConsole) {
    clearUserTokens();
    return {
      status: 'error',
      type: body.type,
      currentAuthority: 'guest',
      currentUser,
    } satisfies API.LoginResult;
  }

  return {
    status: 'ok',
    type: body.type,
    currentAuthority: currentUser?.access,
    token: response.data.accessToken,
    accessToken: response.data.accessToken,
    refreshToken: response.data.refreshToken,
    accessTokenExpiresInSeconds: response.data.accessTokenExpiresInSeconds,
    refreshTokenExpiresInSeconds: response.data.refreshTokenExpiresInSeconds,
    currentUser,
  } satisfies API.LoginResult;
}

/** 刷新双 Token POST /api/v1/auth/token-refreshes */
export async function refreshUserToken(options?: { [key: string]: any }) {
  const refreshToken = getUserRefreshToken();
  if (!refreshToken) {
    clearUserTokens();
    return undefined;
  }

  const response = await request<
    ApiEnvelope<{
      accessToken: string;
      refreshToken: string;
      accessTokenExpiresInSeconds: number;
      refreshTokenExpiresInSeconds: number;
      currentUser: API.CurrentUser;
    }>
  >('/api/v1/auth/token-refreshes', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: {
      refreshToken,
    },
    ...(options || {}),
  });

  setUserTokens(
    response.data.accessToken,
    response.data.refreshToken,
    response.data.accessTokenExpiresInSeconds,
    response.data.refreshTokenExpiresInSeconds,
  );
  return response.data;
}
